/**
 * @author      matt@zumper.com (Matthieu Devlin)
 * @copyright   Copyright (c) 2018, Zumper
 * @description handle follow up modal and analytics and butter action after sending a message
 */

import { BetaFeature, isBetaFeatureEnabled, localStorageSingleton } from '@zcode/nr-utils'
import handleSuccessAnalyticsAndButter, {
  type LightMessageData,
} from '@zcode/nr/es/shared/actions/sendMessage/helper/handleSuccessAnalyticsAndButter'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import type {
  DlcMessagedProperty,
  ExtraThunkArg,
  FiltersState,
  GetState,
  InitialMessageData,
  Listable,
  MessageData,
  SendMessageArgs,
} from '@zcode/state-type'
import type { Dispatch } from 'redux'
import type { O } from 'ts-toolbelt'
import { ALERT_CREATION_TOAST_TYPE } from '../../components/Notification/constants'
import { logger } from '../../logger'
import { setToastType } from '../../modules/currentSearchView/actions'
import { selectResidentQuotesModalOpen } from '../../modules/modals/selectors/residentQuotes'
import { closeModal } from '../../modules/modals/thunks/index'
import { getMMListables } from './getMMListables'
import { handleFollowUpModal } from './handleFollowUpModal'
import { sendMessageAlc } from './sendMessageAlc.action'
import { trackPrepareMultiMessageModal } from './trackPrepareMultiMessageModal'

type Config = O.Pick<
  SendMessageArgs,
  'analyticChannel' | 'dlcMessagedProperties' | 'entity' | 'isMulti' | 'multiListables'
>

export type Props = {
  config: Config
  equalCTAIntent?: SendMessageArgs['equalCTAIntent']
  filters: FiltersState
  formType?: SendMessageArgs['formType']
  messageData: InitialMessageData | MessageData
  selectedFloorplanId?: SendMessageArgs['selectedFloorplanId']
  shouldLimitLeads?: boolean
  showMessageSuccessButter?: boolean
  showPostLeadAfterMM?: boolean
} & (
  | {
      hasInstantTourAvailability?: boolean | undefined
      showTourSuccessButterBar?: false
    }
  | {
      hasInstantTourAvailability: boolean
      showTourSuccessButterBar: boolean
    }
) &
  (
    | {
        showPreLeadSignInModal: true
        sendMessageArgs: SendMessageArgs
      }
    | {
        showPreLeadSignInModal?: false
        sendMessageArgs?: SendMessageArgs
      }
  ) &
  (
    | {
        config: Config & { isMulti?: false }
        messageData: LightMessageData
      }
    | { config: Config & { isMulti: boolean | undefined }; messageData: MessageData }
  )

export const handleFollowUpModalAndSuccessAnalyticsAndButter =
  ({
    equalCTAIntent,
    filters,
    formType,
    selectedFloorplanId,
    sendMessageArgs,
    shouldLimitLeads,
    showMessageSuccessButter,
    showPostLeadAfterMM,
    showPreLeadSignInModal,
    ...configAndMessageData
  }: Props) =>
  async (dispatch: Dispatch, getState: GetState, { subscribe, optimizely }: ExtraThunkArg) => {
    const {
      config: { analyticChannel, entity, isMulti },
      messageData,
    } = configAndMessageData
    const { email, name, origin, phone } = messageData
    // saveSearch/Dlc checkbox was checked and dlc props were passed from messaging form
    const hasDlcProps = !!(formType && messageData && entity)
    // @ts-expect-error
    const { location } = dispatch(getRouteProps())

    if (!selectResidentQuotesModalOpen(getState())) {
      // dispatch to hide message modal
      dispatch(closeModal())
    }

    // Need to backfill similar listings here so that handleSuccessAnalyticsAndButter knows if a
    // multi-message was sent. Checking entity.similar is insufficient since the user may have
    // messaged all them. Only neccessary if the multi message modal could be shown.
    const hideMessageSimilar = localStorageSingleton.get('hideMessageSimilar')
    const possiblyShowMultiMessageModal =
      !isMulti &&
      !hideMessageSimilar &&
      entity &&
      !shouldLimitLeads &&
      (entity?.isRentpath || !entity?.isMmPrevent)

    let similarListings: Listable[] | null = null
    if (possiblyShowMultiMessageModal) {
      similarListings = await dispatch(getMMListables({ entity, filters }))
    }
    const showMMModal = !!similarListings?.length
    let isDlcTestVariation = false
    let dlcMessagedProperties: DlcMessagedProperty[] | undefined = undefined

    if (!showPreLeadSignInModal) {
      dispatch(
        handleSuccessAnalyticsAndButter({
          showTourSuccessButterBar: undefined,
          ...configAndMessageData,
          willShowMultiMessageModal: !!(
            possiblyShowMultiMessageModal &&
            similarListings &&
            similarListings.length > 0
          ),
          showMessageSuccessButter,
        })
      )

      if (isMulti) {
        dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.MESSAGE_SENT))
      }

      if (hasDlcProps) {
        if (
          // if we're not going to show the multi-message modal, call DLC here
          !showMMModal
        ) {
          await dispatch(
            sendMessageAlc({
              entity,
              equalCTAIntent,
              formType,
              messageData: {
                email,
                name,
                origin,
                phone,
              },
              selectedFloorplanId,
            })
          )
        }
        if (
          // if we're showing the multi-message modal activate dlc reorder experiment
          showMMModal
        ) {
          isDlcTestVariation =
            isBetaFeatureEnabled(location, BetaFeature.DLC_ORDER) ||
            optimizely?.activate('WEB_DLC_MM_REORDER_2') === 'On'
          if (
            !isDlcTestVariation
            // if in "Control" call DLC here and get similarListings again to keep the current order of API calls:
            // 1- DLC
            // 2- MM listables
          ) {
            // assign to dlcMessagedProperties to pass them along to the MM modal
            dlcMessagedProperties = await dispatch(
              sendMessageAlc({
                entity,
                equalCTAIntent,
                formType,
                messageData: {
                  email,
                  name,
                  origin,
                  phone,
                },
                selectedFloorplanId,
              })
            )
            similarListings = await dispatch(getMMListables({ entity, filters }))
          }
        }
      }
    }
    const mmListingIds = similarListings?.map((listing) => listing.groupId)
    dispatch(
      trackPrepareMultiMessageModal({
        entity,
        displaying: showMMModal,
        filters,
        mmListingIds,
        shouldLimitLeads,
        showPreLeadSignInModal,
      })
    )

    try {
      if (selectResidentQuotesModalOpen(getState())) {
        // Wait for resident quotes modal to close before opening follow up modal
        await new Promise<void>((resolve) => {
          const unsubscribe = subscribe(() => {
            if (!selectResidentQuotesModalOpen(getState())) {
              resolve()
              unsubscribe()
            }
          })
        })
      }
      // The "then" and "else" branches of this ternary are identical
      // The condition is here just to make TypeScript happy
      dispatch(
        handleFollowUpModal(
          showPreLeadSignInModal
            ? {
                ...configAndMessageData,
                config: {
                  analyticChannel,
                  dlcMessagedProperties,
                  entity,
                },
                equalCTAIntent,
                formType,
                isDlcTestVariation,
                selectedFloorplanId,
                sendMessageArgs,
                shouldLimitLeads,
                showPostLeadAfterMM,
                showPreLeadSignInModal,
                similarListings,
              }
            : {
                ...configAndMessageData,
                config: {
                  analyticChannel,
                  dlcMessagedProperties,
                  entity,
                },
                equalCTAIntent,
                formType,
                isDlcTestVariation,
                selectedFloorplanId,
                sendMessageArgs,
                shouldLimitLeads,
                showPostLeadAfterMM,
                showPreLeadSignInModal,
                similarListings,
              }
        )
      )
    } catch (e: unknown) {
      const args =
        e instanceof Error ? [e] : [new Error('Failed to open messaging follow up modal'), e]
      logger.error(...args)
    }
  }
